import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BulkUpgradePreview } from '@/services/subscriptionUpgradeService';
import { EmailPreviewModal } from '@/components/modals/EmailPreviewModal';
import { UpgradeEmailData } from '@/types/subscriptionUpgrade';
import { BulkUpgradeSummaryCards } from './BulkUpgradeSummaryCards';
import { SubscriptionDetailsList } from './SubscriptionDetailsList';
import { getNextFirstOfMonthLabel } from '@/utils/anchorBilling';

interface BulkUpgradePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedTemplate: string) => void;
  preview: BulkUpgradePreview | null;
  isLoading: boolean;
  businessName?: string;
  anchorBillingEnabled?: boolean;
}

export const BulkUpgradePreviewModal: React.FC<BulkUpgradePreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  preview,
  isLoading,
  businessName: businessNameProp,
  anchorBillingEnabled = false
}) => {
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [selectedEmailData, setSelectedEmailData] = useState<UpgradeEmailData | null>(null);

  if (!preview) return null;

  const processingFeeSavings = preview.processing_fee_savings ?? preview.estimated_additional_revenue ?? (preview.total_processing_fees ?? preview.estimated_additional_revenue ?? 0);

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateString: string) => 
    new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });

  const handleNext = () => {
    if (!preview || preview.subscriptions.length === 0) return;

    const businessName = businessNameProp;
    const firstSubscription = preview.subscriptions[0];
    const emailData: UpgradeEmailData = {
      customerEmail: firstSubscription.customer_email,
      customerName: firstSubscription.customer_email.split('@')[0] || '',
      subscriptionName: `Subscription ${firstSubscription.stripe_subscription_id.slice(-6)}`,
      businessName: businessName,
      currentAmount: firstSubscription.current_amount,
      newAmount: firstSubscription.calculated_new_price,
      gracePeriodEnd: firstSubscription.grace_period_end,
      changePaymentMethodUrl: `${import.meta.env.VITE_APP_URL || 'https://keepmore.co'}/update-payment/${firstSubscription.stripe_subscription_id}`,
      supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || 'support@keepmore.com',
      nextBillingDate: anchorBillingEnabled ? getNextFirstOfMonthLabel() : undefined
    };

    setSelectedEmailData(emailData);
    setIsEmailPreviewOpen(true);
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-full overflow-y-auto" hideClose>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Subscriptions to Update
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <BulkUpgradeSummaryCards
            totalSubscriptions={preview.total_subscriptions}
            processingFeeSavings={processingFeeSavings}
            formatCurrency={formatCurrency}
          />

          <SubscriptionDetailsList
            subscriptions={preview.subscriptions}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline"
           onClick={onClose}
           className=" hover:border-blue-500"
           >
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Next
          </Button>
        </DialogFooter>
      </DialogContent>

      {selectedEmailData && (
        <EmailPreviewModal
          isOpen={isEmailPreviewOpen}
          onClose={() => setIsEmailPreviewOpen(false)}
          emailData={selectedEmailData}
          isMultipleClients={preview.total_subscriptions > 1}
          onConfirm={() => onConfirm('professional')}
          confirmLabel={`Send Emails to ${preview.total_subscriptions} Customers`}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};